import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import { useState, useEffect } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import AppSnackbar from "layouts/notifications/Snackbar";
import { resetCrlPassword } from "services/crlService";
import { useTranslation } from "hooks/useTranslation";
import { translateApiMessage } from "utils/translateApiMessage";

function validateForm(form, t) {
  const errors = {};

  if (!form.password) {
    errors.password = t("crls.modals.validation.newPasswordRequired", "New Password is required");
  }
  if (!form.confirm_password) {
    errors.confirm_password = t(
      "crls.modals.validation.confirmPasswordRequired",
      "Confirm Password is required"
    );
  }
  if (form.password && form.confirm_password && form.password !== form.confirm_password) {
    errors.confirm_password = t(
      "crls.modals.validation.passwordsDontMatch",
      "Passwords do not match"
    );
  }
  return errors;
}

export default function ResetCrlPassword({ open, onClose, crl, onSuccess, title }) {
  const { t } = useTranslation();
  const modalTitle = title || t("crls.modals.resetPassword", "Reset Password");
  const [form, setForm] = useState({ password: "", confirm_password: "" });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, type: "", message: "" });

  const showSnackbar = (type, message) => {
    setSnackbar({ open: true, type, message });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false, type: "", message: "" }));
  };

  useEffect(() => {
    if (open) {
      setForm({ password: "", confirm_password: "" });
      setErrors({});
    }
  }, [open, crl]);

  const handleResetPassword = async () => {
    if (!crl?.id) {
      showSnackbar(
        "error",
        t("crls.modals.validation.crlIdRequired", "Data collector id is required")
      );
      return;
    }

    const validationErrors = validateForm(form, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstError = Object.values(validationErrors)[0];
      showSnackbar("error", firstError);
      return;
    }

    try {
      const result = await resetCrlPassword(crl.id, form.password);
      if (result.status === "success") {
        showSnackbar(
          "success",
          t("crls.notifications.passwordReset", "Password reset successfully")
        );
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 1000);
        return;
      }

      showSnackbar(
        "error",
        translateApiMessage(
          result.message,
          result.code,
          t,
          t("crls.notifications.passwordResetFailed", "Unable to reset password")
        )
      );
    } catch (error) {
      console.error("Failed to reset CRL password:", error);
      showSnackbar(
        "error",
        t("crls.notifications.passwordResetFailed", "Unable to reset password")
      );
    }
  };

  const handleCancel = () => {
    setForm({ password: "", confirm_password: "" });
    setErrors({});
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
        <MDBox variant="gradient" borderRadius="lg" mb={1} textAlign="center">
          <MDTypography variant="h4" fontWeight="medium" mt={3}>
            {modalTitle}
          </MDTypography>
        </MDBox>
        <MDBox pb={3} px={5}>
          <MDBox component="form" role="form">
            <MDBox mb={3}>
              <MDTypography variant="body2" color="text">
                {t(
                  "crls.modals.resetPasswordHint",
                  "Set a new password for the selected data collector. They will use this password on the mobile app."
                )}
              </MDTypography>
            </MDBox>
            <MDBox mb={4}>
              <MDInput
                type="password"
                name="password"
                label={t("crls.modals.fields.newPassword", "New Password")}
                variant="standard"
                value={form.password}
                fullWidth
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                error={Boolean(errors.password)}
              />
            </MDBox>
            <MDBox mb={4}>
              <MDInput
                type="password"
                name="confirm_password"
                label={t("crls.modals.fields.confirmNewPassword", "Confirm New Password")}
                variant="standard"
                value={form.confirm_password}
                fullWidth
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                error={Boolean(errors.confirm_password)}
              />
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDButton variant="gradient" color="info" onClick={handleResetPassword}>
                {t("crls.buttons.reset", "Reset")}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Dialog>
      <AppSnackbar
        type={snackbar.type}
        message={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
      />
    </>
  );
}

ResetCrlPassword.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  crl: PropTypes.object,
  onSuccess: PropTypes.func,
  title: PropTypes.string,
};

ResetCrlPassword.defaultProps = {
  crl: null,
  onSuccess: undefined,
  title: undefined,
};
